import Div from '@/shared/Div';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import Paragraph from '@/shared/Paragraph';
import Span from '@/shared/Span';
import Header from '@/shared/Header';
import { AMC_PLAN_TYPE_NAMES, PLAN_BACKGROUND_MAP } from '@/constants/amc';
import Button from '@/shared/Button';
import FixedWrapper from '@/shared/FixedWrapper';
import date from '@/utils/date';
import { useAppConfigContext } from '@/context/AppContext';
import { DEVICE_STATUS, IR_WORK_ORDER_QUERY } from '@/constants/app';
import Divider from '@/shared/Divider';
import BackgroundBlue from '../Icons/BackgroundBlue';
import successAnimation from './success-lottie.json';
import Lottie from 'lottie-react';
import classNames from 'classnames';
import { getFormattedPrice } from '@/utils/price';
import { useServiceContext } from '@/context/serviceRequestContext';
import { useRouter } from 'next/router';
import useRoutingLoader from '@/hooks/useRoutingLoader';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, GTMPageNames, SCREEN_NAMES } from '@/types/gtm';
import useOnMount from '@/hooks/useOnMount';
import { getPlanEndDate } from '@/utils/amcPlans';
import { AMC_DEVICE_PAGE_URL, STATUS_TRACKING_URL } from '@/configs/constants';

type WelcomeCardProps = {
  tier: string | undefined;
  deviceName: string;
  daysToExpire: number;
  planDuration: number;
  onlyAMCFlow?: boolean;
};

type ButtonProps = {
  showWelcomeCard: boolean;
  onlyAMCFlow: boolean;
  customStyle?: string;
  trackServiceHandler: () => void;
  backToHomeHandler: () => void;
  isLoading: boolean;
};

function calculateExpirationString(daysToExpire: number, planDuration: number) {
  const currentDate = new Date();

  const totalDaysWithYears = daysToExpire + planDuration * 365.25;

  const newDate = new Date(currentDate.setDate(currentDate.getDate() + totalDaysWithYears));

  return date.format(newDate, {
    month: 'long',
    day: undefined,
    year: 'numeric',
  });
}

const savingsContent = (isRental: boolean, status: DEVICE_STATUS, tier: string): string => {
  if (isRental) return 'on your rental agreement.';
  else if (status === DEVICE_STATUS.IW) return 'visit with Free Genuine Service Plan.';
  else if (status === DEVICE_STATUS.IC)
    return `with Genuine AMC • ${tier.toUpperCase() === 'VALUE' ? 'BASE' : tier.toUpperCase()}`;
  return '';
};

const renderWelcomeCard = ({
  tier,
  deviceName,
  daysToExpire,
  planDuration,
  onlyAMCFlow,
}: WelcomeCardProps) => {
  const expiryStatus = calculateExpirationString(daysToExpire, planDuration);
  return (
    <Div
      className={`p-px bg-neutral-50 rounded-cm md:max-w-sm`}
      style={{ background: PLAN_BACKGROUND_MAP[`${tier?.toUpperCase()}`] }}
    >
      <Div className="py-6  bg-white px-4 rounded-cm">
        <Div className="flex flex-col gap-s4">
          <Paragraph className="text-h-3 text-center whitespace-pre-line">
            Welcome to{' '}
            <Span
              className={classNames({
                'text-amc-gold': tier === AMC_PLAN_TYPE_NAMES.GOLD.toLowerCase(),
                'text-amc-silver': tier === AMC_PLAN_TYPE_NAMES.SILVER.toLowerCase(),
                'text-amc-base': tier === AMC_PLAN_TYPE_NAMES.VALUE.toLowerCase(),
                'text-amc-platinum': tier === AMC_PLAN_TYPE_NAMES.PLATINUM.toLowerCase(),
              })}
            >
              {'\n'} Aquaguard Genuine AMC!
            </Span>
          </Paragraph>
          <Paragraph className="text-center whitespace-pre-line text-black/50">{`${
            onlyAMCFlow ? '' : `Your ${deviceName} is in safe hands.`
          } Valid till ${expiryStatus}`}</Paragraph>
        </Div>
      </Div>
    </Div>
  );
};

const renderButton = ({
  showWelcomeCard,
  onlyAMCFlow,
  customStyle = '',
  trackServiceHandler,
  backToHomeHandler,
  isLoading,
}: ButtonProps) => {
  if (showWelcomeCard) {
    return (
      <Span className={`mx-auto w-full  ${customStyle}`}>
        <Button isLoading={isLoading} className="w-full" onClick={backToHomeHandler}>
          Back to home
        </Button>
      </Span>
    );
  }
  return (
    <Span className={`mx-auto w-full ${customStyle}`}>
      <Button isLoading={isLoading} className="w-full" onClick={trackServiceHandler}>
        Track service
      </Button>
    </Span>
  );
};

function PaymentSucessScreen({
  serviceStartTime,
  serviceEndTime,
  status,
  daysToExpire,
  planDuration,
  city,
  title,
  tier,
  deviceName,
  serviceRequestID,
  deviceId,
  showWelcomeCard,
  technicianCode,
  onlyAMCFlow,
  orderId,
  onlyServiceFlow,
  isServiceUpsell,
  planVariantId,
  paymentId,
  discount,
  coupon,
  planValue,
  planType,
  customerCode,
  isRental = false,
  defectType,
  serviceType,
  isRescheduleFlow = false,
  prepaidAmountCollected,
  ABTestVariant,
}: any) {
  const serviceDate = useMemo(
    () =>
      date.format(serviceStartTime, {
        year: undefined,
        day: '2-digit',
        month: 'short',
        hour12: true,
        weekday: 'long',
      }),
    [serviceStartTime],
  );

  const serviceStartingTime = useMemo(
    () => date.getRoundedTime(serviceStartTime),
    [serviceStartTime],
  );
  const serviceEndingTime = useMemo(() => date.getRoundedTime(serviceEndTime), [serviceEndTime]);

  const { fireGTMEvent } = useGTM();

  const {
    state: {
      config: { serviceCost, promoOffers },
    },
    dispatch: appConfigDispatch,
  } = useAppConfigContext();

  const router = useRouter();
  const { state: serviceState } = useServiceContext();
  const { routing, setRouting } = useRoutingLoader();

  const IRWorkOrderFlow = useMemo(() => {
    return router?.query?.[IR_WORK_ORDER_QUERY];
  }, [router]);

  const serviceFlowCheck =
    onlyServiceFlow && (status == DEVICE_STATUS.OW || status == DEVICE_STATUS.OC);
  useOnMount(() => {
    const affiliation =
      technicianCode?.length >= 5 && technicianCode?.length <= 8 ? 'Assisted' : 'Non-Assisted';

    if (onlyServiceFlow) {
      fireGTMEvent(GTMEventNames.SERVICE_BOOKING_SUCCESS_LANDED, {
        service_type: serviceType,
        service_type_selected: defectType,
        upsell_flow: false,
        screen_name: SCREEN_NAMES.SERVICE_BOOKING_SUCCESS,
        customer_type: status,
        asset_id: deviceId,
        product_name: deviceName,
        customer_id: customerCode,
        is_rental: isRental,
        time_slot_selected: `${serviceDate},${serviceStartingTime}-${serviceEndingTime}`,
      });
    } else {
      fireGTMEvent(
        GTMEventNames.IS_AMC_TRANSACTION_SUCCESS,
        {
          pageName: GTMPageNames.AMC_THANKYOU_PAGE,
          loginStatus: 'LoggedIn',
          siteSection: 'AMC',
          supportCategory: 'AMC',
          navigated_from: 'Buy AMC Plan',
          paymentMode: 'RazorPay',
          amc_plan_duration: planDuration,
          order_id: orderId,
          customer_id: customerCode,
          amc_plan_start_date: getPlanEndDate(daysToExpire, 0).toISOString(),
          amc_plan_end_date: getPlanEndDate(daysToExpire, planDuration).toISOString(),
          upsell_flow: !onlyAMCFlow,
          amc_plan_value: planValue,
          asset_id: deviceId,
          product_name: deviceName,
          customer_type: status,
          amc_tier: tier,
          is_assisted: affiliation === 'Assisted',
          ab_test: ABTestVariant,
        },
        {
          webengage: {
            fire: true,
            isDirect: true,
          },
        },
      );
      fireGTMEvent(GTMEventNames.AMC_PAYMENT_STATUS, {
        transaction_status: 'success',
        amc_plan_value: planValue,
        amc_plan_duration: planDuration,
        order_id: orderId,
        amc_tier: tier,
        customer_type: status,
        customer_id: customerCode,
        product_name: deviceName,
        asset_id: deviceId,
        ab_test: ABTestVariant,
      });
      if (affiliation === 'Non-Assisted') {
        fireGTMEvent(GTMEventNames.IS_UNASSISTED_AMC_TRANSACTION_SUCCESS, {
          amc_plan_duration: planDuration,
          order_id: orderId,
          customer_id: customerCode,
          amc_plan_start_date: getPlanEndDate(daysToExpire, 0).toISOString(),
          amc_plan_end_date: getPlanEndDate(daysToExpire, planDuration).toISOString(),
          upsell_flow: !onlyAMCFlow,
          amc_plan_value: planValue,
          asset_id: deviceId,
          product_name: deviceName,
          customer_type: status,
          amc_tier: tier,
          ab_test: ABTestVariant,
        });
      }
      fireGTMEvent(GTMEventNames.PURCHASE, {
        ecommerce: {
          transaction_id: paymentId,
          value: planValue,
          coupon: coupon && coupon?.length > 0 ? coupon[0] : '',
          ab_test: ABTestVariant,
          items: [
            {
              item_id: planVariantId,
              item_name: planType,
              item_category: 'AMC',
              affiliation: affiliation,
              coupon: coupon && coupon.length > 0 ? coupon[0] : '',
              discount: discount,
              price: planValue,
              item_brand: 'Aquaguard/Forbes',
              quantity: 1,
            },
          ],
        },
      });
    }
  });

  useEffect(() => {
    if (promoOffers) {
      setTimeout(() => {
        appConfigDispatch.togglePromoOffer();
      }, 3000);
    }
  }, [promoOffers]);

  const trackServiceHandler = useCallback(() => {
    setRouting(true);

    if (IRWorkOrderFlow) {
      router
        .push({
          pathname: STATUS_TRACKING_URL,
          query: {
            reqId: router?.query?.reqID,
            [IR_WORK_ORDER_QUERY]: true,
          },
        })
        .then(() => setRouting(false));
    } else {
      router
        .push({
          pathname: STATUS_TRACKING_URL,
          query: {
            reqId: serviceRequestID,
            assetid: deviceId,
            assets: true,
          },
        })
        .then(() => setRouting(false));
    }
  }, [router, serviceRequestID, deviceId, setRouting]);

  const backToHomeHandler = useCallback(() => {
    setRouting(true);
    router
      .push({
        pathname: AMC_DEVICE_PAGE_URL,
        query: {
          assetid: deviceId,
        },
      })
      .then(() => setRouting(false));
  }, [setRouting, deviceId, router]);

  return (
    <Fragment>
      <BackgroundBlue className="fixed max-h-screen left-0 top-0 overflow-hidden hidden lg:block" />
      <BackgroundBlue className="fixed max-h-screen right-0 top-0 overflow-hidden  lg:block rotate-180" />
      <Div className="w-full max-w-screen-sm mx-auto ">
        <Div className="flex flex-col gap-s5 relative ">
          <Div className="flex flex-col gap-s5 md:gap-s22 md:justify-center md:items-center">
            <Div className="flex flex-col md:flex-row md:gap-s5">
              <Div className="flex justify-center items-center">
                <Lottie
                  className=" md:hidden w-[150px] h-[150px]"
                  animationData={successAnimation}
                  loop={false}
                />
                <Lottie
                  className=" hidden md:inline w-[44px] h-[44px]"
                  animationData={successAnimation}
                  loop={false}
                />
              </Div>
              <Header size="xl" className="text-center whitespace-pre-line text-black/70">
                {title}
              </Header>
            </Div>
            <Div className="flex flex-col gap-s6 md:gap-s22 md:justify-center md:items-center">
              {!onlyAMCFlow ? (
                <Div className="flex flex-col gap-s6">
                  <Paragraph className="text-center text-body-lg">
                    You will receive an SMS confirmation when a technician is assigned.{' '}
                  </Paragraph>
                  <Divider />
                </Div>
              ) : null}
              <Div className="flex flex-col gap-s5">
                {onlyAMCFlow ? (
                  <Paragraph className="text-center text-black/50">
                    Your invoice will be available on your app in about 5 minutes.
                  </Paragraph>
                ) : (
                  <Div className="flex flex-col gap-s2">
                    <Paragraph className="text-callout-normal text-center">
                      Your preferred slot:
                    </Paragraph>
                    <Paragraph className="text-center text-black/50">
                      {serviceDate}, {serviceStartingTime}-{serviceEndingTime}{' '}
                      {IRWorkOrderFlow ? null : `,${city}`}
                    </Paragraph>
                  </Div>
                )}
                <Paragraph className="text-center whitespace-pre-line text-black/50">
                  {!['reinstall', 'uninstall'].includes(serviceState.serviceType) &&
                  !serviceFlowCheck &&
                  !onlyAMCFlow &&
                  !IRWorkOrderFlow &&
                  (isServiceUpsell ||
                    isRental ||
                    status === DEVICE_STATUS.IC ||
                    status === DEVICE_STATUS.IW) ? (
                    <>
                      <Divider className="mb-4 w-4/6 md:w-full mx-auto" />
                      {'You'}{' '}
                      <Span className="text-blue-400 font-semibold">{`saved ${getFormattedPrice(
                        serviceCost,
                      )}`}</Span>{' '}
                      {`on this service \n ${savingsContent(isRental, status, tier)}`}
                    </>
                  ) : serviceFlowCheck &&
                    !['reinstall', 'uninstall'].includes(serviceState.serviceType) &&
                    !(isRescheduleFlow && Number(prepaidAmountCollected) > 0) ? (
                    <Span className="text-blue-400">
                      {getFormattedPrice(serviceCost)} payable at the time of visit
                    </Span>
                  ) : null}
                </Paragraph>
              </Div>
              {showWelcomeCard && deviceName && tier
                ? renderWelcomeCard({ tier, deviceName, daysToExpire, planDuration, onlyAMCFlow })
                : null}
              {renderButton({
                showWelcomeCard,
                onlyAMCFlow,
                customStyle: ' hidden md:inline-block',

                trackServiceHandler,
                backToHomeHandler,
                isLoading: routing,
              })}
            </Div>
          </Div>
        </Div>
        <FixedWrapper className="md:hidden p-6 pt-4 bg-white shadow-subtle">
          {renderButton({
            showWelcomeCard,
            onlyAMCFlow,
            trackServiceHandler,
            backToHomeHandler,
            isLoading: routing,
          })}
        </FixedWrapper>
      </Div>
    </Fragment>
  );
}

export default PaymentSucessScreen;
